import React, { useState, useEffect } from 'react';
import Select from '../../components/ui/Select';
import { useToast } from '../../components/ui/Toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import notificationService from '../../services/notificationService';
import TemplateLibrary from './components/TemplateLibrary';
import TemplateEditor from './components/TemplateEditor';
import TemplatePreview from './components/TemplatePreview';
import { useAuth } from '../../contexts/AuthContext';

const EmailTemplateManagementSystem = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedConferenceId, setSelectedConferenceId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    const conferenceId = localStorage?.getItem('selectedConferenceId');
    if (conferenceId) {
      setSelectedConferenceId(conferenceId);
      loadTemplates(conferenceId);
    } else {
      setLoading(false);
    }
  }, []);

  const loadTemplates = async (conferenceId) => {
    try {
      setLoading(true);
      const templatesData = await notificationService?.getTemplates(conferenceId);
      setTemplates(templatesData || []);
    } catch (err) {
      console.error('Load templates error:', err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedTemplate(null);
    setShowEditor(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setSelectedTemplate(null);
  };

  const handleSaveSuccess = () => {
    loadTemplates(selectedConferenceId);
    handleCloseEditor();
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm(t('emailTemplateManagement.confirmDelete'))) return;

    try {
      await notificationService?.deleteTemplate(templateId);
      loadTemplates(selectedConferenceId);
    } catch (err) {
      toastError(err?.message || t('emailTemplateManagement.errors.failedToDelete'));
    }
  };

  const filteredTemplates = templates?.filter(template => {
    const matchesSearch = template?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         template?.subject?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = filterCategory === 'all' || template?.notificationType === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (!selectedConferenceId) {
    // This was the only screen in the app with a sidebar — a whole navigation
    // pattern that appeared on one route out of thirty-one. The conference bar
    // now covers it, so the page uses the same shell as everything else.
    return (
      <div className="min-h-screen bg-background">
        <div className="flex flex-col">
          <UniversalNavbar />
          <main className="flex-1 overflow-auto p-8">
            <div className="max-w-2xl mx-auto text-center py-12">
              <Icon name="AlertCircle" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">{t('emailTemplateManagement.noConference.title')}</h2>
              <p className="text-muted-foreground mb-6">
                {t('emailTemplateManagement.noConference.message')}
              </p>
              <Button onClick={() => navigate('/user-dashboard')}>
                {t('emailTemplateManagement.noConference.goToDashboard')}
              </Button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="flex flex-col">
        <UniversalNavbar />
        <main className="flex-1 overflow-auto">
          <div className="h-full">
            {showEditor ? (
              <div className="h-full grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
                <div className="overflow-auto">
                  <TemplateEditor
                    conferenceId={selectedConferenceId}
                    template={selectedTemplate}
                    onClose={handleCloseEditor}
                    onSave={handleSaveSuccess}
                  />
                </div>
                <div className="overflow-auto">
                  <TemplatePreview template={selectedTemplate} />
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-foreground">{t('emailTemplateManagement.title')}</h1>
                    <p className="text-muted-foreground mt-1">
                      {t('emailTemplateManagement.subtitle')}
                    </p>
                  </div>
                  <Button onClick={handleCreateNew}>
                    <Icon name="Plus" size={18} className="mr-2" />
                    {t('emailTemplateManagement.createTemplate')}
                  </Button>
                </div>

                {/* Search and Filter */}
                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative">
                      <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder={t('emailTemplateManagement.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e?.target?.value)}
                        className="pl-10"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Filter" size={18} className="text-muted-foreground" />
                      <Select
                        value={filterCategory}
                        onChange={setFilterCategory}
                        className="flex-1"
                        options={[
                          { value: 'all', label: t('emailTemplateManagement.filters.allCategories') },
                          { value: 'assignment', label: t('emailTemplateManagement.filters.reviewerAssignments') },
                          { value: 'decision', label: t('emailTemplateManagement.filters.paperDecisions') },
                          { value: 'deadline_reminder', label: t('emailTemplateManagement.filters.deadlineReminders') },
                          { value: 'status_update', label: t('emailTemplateManagement.filters.statusUpdates') },
                          { value: 'custom', label: t('emailTemplateManagement.filters.generalCommunications') },
                        ]}
                      />
                    </div>
                  </div>
                </div>

                {/* Template Library */}
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Icon name="Loader2" size={48} className="text-primary animate-spin" />
                  </div>
                ) : (
                  <TemplateLibrary
                    templates={filteredTemplates}
                    onEdit={handleEditTemplate}
                    onDelete={handleDeleteTemplate}
                  />
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default EmailTemplateManagementSystem;